import { aicisSupabase, abvigSupabase } from './supabaseClient';

// ============================================
// AICIS API
// ============================================
export const aicisAPI = {
  // Diplomatic Sign In (registrations table)
  getDiplomaticSignIns: async () => {
    try {
      console.log('Fetching diplomatic sign ins from Supabase...');
      const { data, error } = await aicisSupabase
        .from('registrations')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      console.log('Diplomatic sign ins fetched:', data);
      return data;
    } catch (error) {
      console.error('Error in getDiplomaticSignIns:', error);
      throw error;
    }
  },

  updateDiplomaticSignInStatus: async (id, status) => {
    const { data, error } = await aicisSupabase
      .from('registrations')
      .update({ status })
      .eq('id', id)
      .select();
    if (error) throw error;
    return data;
  },

  deleteDiplomaticSignIn: async (id) => {
    const { error } = await aicisSupabase
      .from('registrations')
      .delete()
      .eq('id', id);
    if (error) throw error;
    return true;
  },

  // Speakers (speakers_forms table)
  getSpeakers: async () => {
    try {
      console.log('Fetching speakers from Supabase...');
      const { data, error } = await aicisSupabase
        .from('speakers_forms')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      console.log('Speakers fetched:', data);
      return data;
    } catch (error) {
      console.error('Error in getSpeakers:', error);
      throw error;
    }
  },

  updateSpeakerStatus: async (id, status) => {
    const { data, error } = await aicisSupabase
      .from('speakers_forms')
      .update({ status })
      .eq('id', id)
      .select();
    if (error) throw error;
    return data;
  },

  deleteSpeaker: async (id) => {
    const { error } = await aicisSupabase
      .from('speakers_forms')
      .delete()
      .eq('id', id);
    if (error) throw error;
    return true;
  },

  // Event Registration (transactions table)
  getEventRegistrations: async () => {
    const { data, error } = await aicisSupabase
      .from('transactions')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data;
  },

  updateEventRegistration: async (id, updates) => {
    const { data, error } = await aicisSupabase
      .from('transactions')
      .update(updates)
      .eq('id', id)
      .select();
    if (error) throw error;
    return data;
  },

  // Affiliate Purchases (affiliate_purchases table)
  getAffiliatePurchases: async () => {
    const { data, error } = await aicisSupabase
      .from('affiliate_purchases')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data;
  },

  updateAffiliatePurchase: async (id, updates) => {
    const { data, error } = await aicisSupabase
      .from('affiliate_purchases')
      .update(updates)
      .eq('id', id)
      .select();
    if (error) throw error;
    return data;
  },

  // Policy Agreements
  getPolicyAgreements: async () => {
    const { data, error } = await aicisSupabase
      .from('policy_agreements')
      .select('*')
      .order('agreed_at', { ascending: false });
    if (error) throw error;
    return data;
  },

  // System Settings
  getSystemSettings: async () => {
    const { data, error } = await aicisSupabase
      .from('system_settings')
      .select('*')
      .limit(1)
      .single();
    if (error) throw error;
    return data;
  },

  updateSystemSettings: async (settings) => {
    const { data, error } = await aicisSupabase
      .from('system_settings')
      .update(settings)
      .eq('id', settings.id)
      .select();
    if (error) throw error;
    return data;
  },

  // Admin Authentication
  verifyAdminLogin: async (username, password) => {
    try {
      const { data, error } = await aicisSupabase
        .rpc('verify_admin_login', {
          p_username: username,
          p_password: password
        });
      
      if (error) {
        console.error('Error verifying login:', error);
        throw error;
      }
      
      return data;
    } catch (error) {
      console.error('Login verification failed:', error);
      throw error;
    }
  },

  updateAdminPassword: async (currentPassword, newPassword) => {
    try {
      const { data, error } = await aicisSupabase
        .rpc('update_admin_password', {
          p_current_password: currentPassword,
          p_new_password: newPassword
        });
      
      if (error) {
        console.error('Error updating password:', error);
        throw error;
      }
      
      return data;
    } catch (error) {
      console.error('Password update failed:', error);
      throw error;
    }
  },

  getAdminSettings: async () => {
    const { data, error } = await aicisSupabase
      .from('admin_settings')
      .select('username, updated_at')
      .single();
    
    if (error) throw error;
    return data;
  },

  // Dashboard Stats
  getDashboardStats: async () => {
    const [registrations, speakers, transactions, purchases, agreements] = await Promise.all([
      aicisSupabase.from('registrations').select('*', { count: 'exact', head: true }),
      aicisSupabase.from('speakers_forms').select('*', { count: 'exact', head: true }),
      aicisSupabase.from('transactions').select('*', { count: 'exact', head: true }),
      aicisSupabase.from('affiliate_purchases').select('*', { count: 'exact', head: true }),
      aicisSupabase.from('policy_agreements').select('*', { count: 'exact', head: true }),
    ]);

    const pendingSignIns = await aicisSupabase
      .from('registrations')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'pending');

    const pendingSpeakers = await aicisSupabase
      .from('speakers_forms')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'pending');

    const pendingTransactions = await aicisSupabase
      .from('transactions')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'pending');

    const pendingPurchases = await aicisSupabase
      .from('affiliate_purchases')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'pending');

    return {
      totalSignIns: registrations.count || 0,
      totalSpeakers: speakers.count || 0,
      totalTransactions: transactions.count || 0,
      totalPurchases: purchases.count || 0,
      totalAgreements: agreements.count || 0,
      pendingSignIns: pendingSignIns.count || 0,
      pendingSpeakers: pendingSpeakers.count || 0,
      pendingTransactions: pendingTransactions.count || 0,
      pendingPurchases: pendingPurchases.count || 0,
    };
  },
};

// ============================================
// ABVIG API
// ============================================
export const abvigAPI = {
  getContactMessages: async () => {
    const { data, error } = await abvigSupabase
      .from('contact_messages')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data;
  },

  updateContactMessageStatus: async (id, status) => {
    const { data, error } = await abvigSupabase
      .from('contact_messages')
      .update({ status })
      .eq('id', id)
      .select();
    if (error) throw error;
    return data;
  },

  getInternApplications: async () => {
    const { data, error } = await abvigSupabase
      .from('intern_applications')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data;
  },

  updateInternApplicationStatus: async (id, status) => {
    const { data, error } = await abvigSupabase
      .from('intern_applications')
      .update({ status })
      .eq('id', id)
      .select();
    if (error) throw error;
    return data;
  },

  getPartnershipInquiries: async () => {
    const { data, error } = await abvigSupabase
      .from('partnership_inquiries')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data;
  },

  updatePartnershipStatus: async (id, status) => {
    const { data, error } = await abvigSupabase
      .from('partnership_inquiries')
      .update({ status })
      .eq('id', id)
      .select();
    if (error) throw error;
    return data;
  },

  getSubscribers: async () => {
    const { data, error } = await abvigSupabase
      .from('subscribers')
      .select('*')
      .order('subscribed_at', { ascending: false });
    if (error) throw error;
    return data;
  },

  getDashboardStats: async () => {
    const [messages, applications, partnerships, subscribers] = await Promise.all([
      abvigSupabase.from('contact_messages').select('*', { count: 'exact', head: true }),
      abvigSupabase.from('intern_applications').select('*', { count: 'exact', head: true }),
      abvigSupabase.from('partnership_inquiries').select('*', { count: 'exact', head: true }),
      abvigSupabase.from('subscribers').select('*', { count: 'exact', head: true }),
    ]);

    const pendingMessages = await abvigSupabase
      .from('contact_messages')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'pending');

    const pendingApplications = await abvigSupabase
      .from('intern_applications')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'pending');

    const pendingPartnerships = await abvigSupabase
      .from('partnership_inquiries')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'pending');

    return {
      totalMessages: messages.count || 0,
      totalApplications: applications.count || 0,
      totalPartnerships: partnerships.count || 0,
      totalSubscribers: subscribers.count || 0,
      pendingMessages: pendingMessages.count || 0,
      pendingApplications: pendingApplications.count || 0,
      pendingPartnerships: pendingPartnerships.count || 0,
    };
  },
};